import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useUser } from "@/contexts/UserContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserData } = useUser();
  const hasProcessed = useRef(false);
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Check for direct Google OAuth token in query params
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        
        if (token) {
          // Direct Google OAuth - token already set as cookie by backend
          // Just fetch user data
          const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            withCredentials: true
          });
          
          if (response.data) {
            setUserData(response.data, token);
            toast.success(`Welcome, ${response.data.name}!`);
            navigate("/dashboard", { replace: true });
            return;
          }
        }
        
        // Fallback: Check for Emergent Auth session_id in hash
        const hash = window.location.hash;
        console.log("Auth callback hash:", hash);
        
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error("No session ID in hash:", hash);
          setError("No session ID found in callback URL");
          toast.error("No session ID found");
          setTimeout(() => navigate("/login"), 2000);
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log("Session ID extracted:", sessionId);

        // Exchange session_id for user data
        const response = await axios.post(
          `${BACKEND_URL}/api/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        console.log("Session response:", response.data);

        if (response.data.success && response.data.user) {
          // Set user data in context immediately
          setUserData(response.data.user, response.data.session_token);
          
          toast.success(`Welcome, ${response.data.user.name}!`);
          // Navigate to dashboard
          navigate("/dashboard", { replace: true });
        } else {
          throw new Error("Authentication response missing user data");
        }
      } catch (error) {
        console.error("Auth error:", error);
        const errorMsg = error.response?.data?.detail || error.message || "Authentication failed";
        setError(errorMsg);
        toast.error(errorMsg);
        setTimeout(() => navigate("/login"), 2000);
      } finally {
        setProcessing(false);
      }
    };

    processAuth();
  }, [navigate, setUserData]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="auth-callback-error">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-lg text-red-600 mb-2" data-testid="auth-callback-error-title">Authentication Failed</p>
          <p className="text-gray-500" data-testid="auth-callback-error-message">{error}</p>
          <p className="text-sm text-gray-400 mt-2" data-testid="auth-callback-redirect">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="auth-callback-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600" data-testid="auth-callback-loading-text">Signing you in...</p>
        </div>
      </div>
    );
  }

  return null;
}
